<template>
    <div id="app">

        <!-- Record Details Popup -->
        <div v-if="selectedRecord" class="simple-popup" @click.self="selectedRecord = null">
            <div class="popup-content wide-popup">
                <div class="popup-header">
                    <h3>Dispensing API Transaction Details</h3>
                    <button @click="selectedRecord = null" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="detail-sections">
                        <div class="detail-section">
                            <h4>Transaction Information</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>ID:</strong> {{ selectedRecord.ID }}</div>
                                <div class="detail-row"><strong>Order ID:</strong> {{ selectedRecord.OrderId }}</div>

                                <div class="detail-row"><strong>Store:</strong> {{ selectedRecord.Store }}</div>
                                <div class="detail-row"><strong>GUID:</strong> {{ selectedRecord.TransactionGUID }}</div>
                                <div class="detail-row"><strong>Request Date:</strong> {{
                                    formatDateTime(selectedRecord.RequestDate) }}</div>
                                <div class="detail-row"><strong>Response Date:</strong> {{
                                    formatDateTime(selectedRecord.ResponseDate) }}</div>
                                <div class="detail-row"><strong>Status Code:</strong> {{ selectedRecord.StatusCode }}         
                                </div>
                                <div class="detail-row"><strong>Transactoion ID:</strong> {{ selectedRecord.TransactionID }}</div>
                            </div>
                        </div>
                        <div class="detail-section equipment-section">
                            <h4><span class="source-icon">🚛</span> Vehicle Details</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Registration:</strong> {{
                                    selectedRecord.VehicleRegistration }}</div>
                                <div class="detail-row"><strong>Make:</strong> {{ selectedRecord.VehicleMake || 'N/A' }}
                                </div>
                                <div class="detail-row"><strong>Model:</strong> {{ selectedRecord.VehicleModel || 'N/A'
                                    }}</div>
                                <div class="detail-row"><strong>Odometer:</strong> {{
                                    selectedRecord.VehicleOdometerReading || 'N/A' }}</div>
                            </div>
                        </div>
                        <div class="detail-section cost-section">
                            <h4><span class="source-icon">⛽</span> Product & Quantities</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Product:</strong> {{ selectedRecord.ProductName }}</div>
                                <div class="detail-row"><strong>Qty Ordered:</strong> {{
                                    formatVolume(selectedRecord.QuantityOrdered) }} L</div>
                                <div class="detail-row"><strong>Qty Dispensed:</strong> {{
                                    formatVolume(selectedRecord.QuantityDispensed) }} L</div>
                            </div>
                        </div>
                        <div class="detail-section personnel-section">
                            <h4><span class="source-icon">👤</span> Driver & Status</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Driver:</strong> {{ selectedRecord.DriverFName }} {{
                                    selectedRecord.DriverLName }}</div>
                                <div class="detail-row"><strong>Driver License:</strong> {{ selectedRecord.DriverLicense
                                    || 'N/A' }}</div>
                                <div class="detail-row"><strong>Success:</strong> {{ selectedRecord.Success }}</div>
                                <div class="detail-row"><strong>Error:</strong><br> {{ selectedRecord.ErrorMessage || 'None' }}</div>
                                <div class="detail-row"><strong>Response Body:</strong> {{ selectedRecord.ResponseBody
                                    || 'N/A' }}</div>
                            </div>
                        </div>
                        <!-- Dynamic Fields Table -->
                        <div v-if="selectedRecord.fieldsArray && selectedRecord.fieldsArray.length" class="detail-section fields-section">
                            <h4><span class="source-icon">📋</span> Fields</h4>
                            <table class="fields-table">
                                <thead>
                                    <tr>
                                        <th>Key</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(f, i) in selectedRecord.fieldsArray" :key="i">
                                        <td class="field-key">{{ f.Key || f.key }}</td>
                                        <td class="field-value">{{ f.Value || f.value }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Drill-down Popup -->
        <div v-if="showDrillDown" class="simple-popup drill-down-overlay">
            <div class="popup-content drill-down-popup" ref="drillDownPopup"
                :style="{ left: drillDownPosition.x + 'px', top: drillDownPosition.y + 'px' }">
                <div class="popup-header drill-down-header" @mousedown="startDrag" @touchstart="startDrag">
                    <div class="header-info">
                        <h3>{{ drillDownTitle }}</h3>
                        <span class="popup-date-range">Date: {{ formatDateForDisplay(filters.fromDate) }} - {{
                            formatDateForDisplay(filters.toDate) }}</span>
                    </div>
                    <button @click="closeDrillDown" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="drill-down-summary">
                        <div class="summary-card">
                            <span class="summary-label">{{ drillDownGroup }}</span>
                            <span class="summary-value-large">{{ drillDownData.groupName }}</span>
                        </div>
                        <div class="summary-stats">
                            <div class="stat-item">
                                <span class="stat-label">Total Dispensed:</span>
                                <span class="stat-value">{{ formatVolume(drillDownData.totalDispensed) }} L</span>
                            </div>
                            <div class="stat-item">
                                <span class="stat-label">Transactions:</span>
                                <span class="stat-value">{{ drillDownData.records.length }}</span>
                            </div>
                            <div class="stat-item">
                                <span class="stat-label">Successful:</span>
                                <span class="stat-value">{{ drillDownData.successCount }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="drill-down-actions">
                        <button class="action-btn excel-btn" @click="exportDrillDownCSV"><span>📊</span> CSV
                            Export</button>
                        <button class="action-btn print-btn" @click="printDrillDown"><span>🖨️</span> Print</button>
                    </div>
                    <div class="drill-down-grid">
                        <table class="drill-down-table">
                            <thead>
                                <tr>
                                    <th>Request Date</th>
                                    <th>Registration</th>
                                    <th>Driver</th>
                                    <th>Product</th>
                                    <th>Ordered</th>
                                    <th>Dispensed</th>
                                    <th>Status</th>
                                    <th>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(record, index) in drillDownData.records" :key="index"
                                    @click="showRecordDetails(record)" class="drill-down-row">
                                    <td>{{ formatDateTime(record.RequestDate) }}</td>
                                    <td><code class="registration-code">{{ record.VehicleRegistration }}</code></td>
                                    <td>{{ record.DriverFName }} {{ record.DriverLName }}</td>
                                    <td><span :class="['product-badge-small', getProductClass(record.ProductName)]">{{
                                            record.ProductName }}</span></td>
                                    <td>{{ formatVolume(record.QuantityOrdered) }} L</td>
                                    <td class="volume-cell-drill">{{ formatVolume(record.QuantityDispensed) }} L</td>
                                    <td><span :class="['status-pill', getStatusClass(record.StatusCode)]">{{
                                            record.StatusCode || 'N/A' }}</span></td>
                                    <td>{{ record.ErrorMessage || '—' }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="drill-down-totals">
                            <span class="total-label">Total Dispensed:</span>
                            <span class="total-value">{{ formatVolume(drillDownData.totalDispensed) }} L</span>
                        </div>
                    </div>
                    <div class="drill-down-footer">
                        <span class="record-count">{{ drillDownData.records.length }} of {{ drillDownData.records.length
                            }} items</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Header -->
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon"><span>⛽</span></div>
                    <h1 class="dashboard-title">FAMS Dispensing API Monitor</h1>
                </div>
                <div class="header-controls">
                    <div class="loader-controls">
                        <div class="loader-toggle">
                            <label class="toggle-label">
                                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle"
                                    class="toggle-checkbox">
                                <span class="toggle-text">Auto Refresh</span>
                            </label>
                        </div>
                        <div v-if="autoRefreshEnabled" class="refresh-interval">
                            <label class="interval-label">
                                <span class="interval-text">Every:</span>
                                <select v-model="refreshInterval" @change="handleRefreshIntervalChange"
                                    class="interval-select">
                                    <option :value="30000">30 sec</option>
                                    <option :value="60000">1 min</option>
                                    <option :value="300000">5 min</option>
                                    <option :value="600000">10 min</option>
                                </select>
                            </label>
                        </div>
                        <button class="header-refresh-btn" @click="refreshData">
                            <span class="refresh-icon">🔄</span><span>Refresh</span>
                        </button>
                    </div>
                    <div class="live-indicator">
                        <div :class="['live-dot', { 'auto-refresh': autoRefreshEnabled }]"></div>
                        <span style="color: white; font-size: 0.875rem;">
                            {{ autoRefreshEnabled ? 'Live (' + formatRefreshInterval() + ')' : 'Live' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">

            <!-- Statistics Section -->
            <div class="stats-section">
                <div class="stats-header">
                    <h3 class="stats-title">API Dispensing Overview</h3>
                    <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
                        <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
                        {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
                    </button>
                </div>
                <transition name="slide-fade">
                    <div v-if="statsVisible" class="stats-wrapper">
                        <div class="stats-container">
                            <div class="stat-card clickable-stat total-stat" @click="clearAllFilters">
                                <div class="stat-label">Total Transactions</div>
                                <div class="stat-value">{{ summaryStats.totalTransactions }}</div>
                                <div class="stat-change positive"><span class="stat-icon">📝</span><span>All
                                        records</span></div>
                                <div class="stat-hint">Click to show all</div>
                            </div>
                            <div class="stat-card volume-stat">
                                <div class="stat-label">Total Dispensed</div>
                                <div class="stat-value" style="color: #4CAF50;">{{
                                    formatVolume(summaryStats.totalDispensed) }} L</div>
                                <div class="stat-change"><span class="stat-icon">⛽</span><span>Fuel dispensed</span>
                                </div>
                            </div>
                            <div class="stat-card cost-stat">
                                <div class="stat-label">Successful</div>
                                <div class="stat-value" style="color: #27AE60;">{{ summaryStats.successCount }}</div>
                                <div class="stat-change positive"><span class="stat-icon">✅</span><span>{{
                                        summaryStats.successRate }}% success rate</span></div>
                            </div>
                            <div class="stat-card equipment-stat">
                                <div class="stat-label">Failed / Errors</div>
                                <div class="stat-value" style="color: #E74C3C;">{{ summaryStats.failedCount }}</div>
                                <div class="stat-change negative"><span class="stat-icon">❌</span><span>Unsuccessful
                                        attempts</span></div>
                            </div>
                            <div class="stat-card product-stat">
                                <div class="stat-label">Unique Vehicles</div>
                                <div class="stat-value" style="color: #2196F3;">{{ summaryStats.uniqueVehicles }}</div>
                                <div class="stat-change"><span class="stat-icon">🚛</span><span>Active vehicles</span>
                                </div>
                            </div>
                            <div class="stat-card average-stat">
                                <div class="stat-label">Avg Dispensed</div>
                                <div class="stat-value" style="color: #E74C3C;">{{
                                    formatVolume(summaryStats.avgDispensed) }} L</div>
                                <div class="stat-change"><span class="stat-icon">📊</span><span>Per transaction</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Filters Section -->
            <div class="search-section" :class="{ 'collapsed': !filtersVisible }">
                <div class="filter-header">
                    <h3 class="filter-title">Data Filters & Reports</h3>
                    <div class="filter-header-buttons">
                        <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
                            <span class="toggle-icon">{{ filtersVisible ? '📁' : '📂' }}</span>
                            {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
                        </button>
                        <button class="reset-filter-header-btn" @click="resetFilters">
                            <span class="reset-icon">↻</span> Reset Filters
                        </button>
                    </div>
                </div>
                <transition name="slide-fade">
                    <div v-if="filtersVisible" class="search-controls-wrapper">
                        <div class="search-controls">
                            <!-- <div class="search-group">
                                <label class="search-label">From Date</label>
                                <input type="date" class="search-input" v-model="filters.fromDate"
                                    @change="applyFilters">
                            </div>
                            <div class="search-group">
                                <label class="search-label">To Date</label>
                                <input type="date" class="search-input" v-model="filters.toDate" @change="applyFilters">
                            </div> -->
                            <div class="search-group">
                                <label class="search-label">Report Type</label>
                                <select v-model="filters.reportType" @change="handleReportTypeChange"
                                    class="search-input">
                                    <option value="general">General View</option>
                                    <option value="usage-vehicle">Usage per Vehicle</option>
                                    <option value="usage-product">Usage per Product</option>
                                    <option value="usage-driver">Usage per Driver</option>
                                    <option value="usage-status">Usage per Status Code</option>
                                    <option value="usage-make">Usage per Vehicle Make</option>
                                </select>
                            </div>
                            <div class="search-group">
                                <label class="search-label">Success Status</label>
                                <select v-model="filters.successFilter" @change="applyFilters" class="search-input">
                                    <option value="">All</option>
                                    <option value="Yes">Successful Only</option>
                                    <option value="No">Failed Only</option>
                                </select>
                            </div>
                            <!-- Vehicle Multi-Select -->
                            <div class="search-group">
                                <label class="search-label">
                                    Vehicle Registration
                                    <span class="multi-badge orange-badge">({{ filters.vehicleIds.length }}/{{
                                        uniqueVehicles.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('vehicles')">
                                        <span v-if="filters.vehicleIds.length === 0" class="placeholder">All
                                            Vehicles</span>
                                        <span v-else-if="filters.vehicleIds.length === uniqueVehicles.length"
                                            class="selected-items">All vehicles selected</span>
                                        <span v-else class="selected-items">{{ filters.vehicleIds.length }}
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.vehicles" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllVehicles"
                                                    :checked="filters.vehicleIds.length === uniqueVehicles.length">
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="v in uniqueVehicles" :key="v.id">
                                                <input type="checkbox" :value="v.id" v-model="filters.vehicleIds"
                                                    @change="applyFilters">
                                                <span>{{ v.name }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Product Multi-Select -->
                            <div class="search-group">
                                <label class="search-label">
                                    Product
                                    <span class="multi-badge orange-badge">({{ filters.productIds.length }}/{{
                                        uniqueProducts.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('products')">
                                        <span v-if="filters.productIds.length === 0" class="placeholder">All
                                            Products</span>
                                        <span v-else-if="filters.productIds.length === uniqueProducts.length"
                                            class="selected-items">All products selected</span>
                                        <span v-else class="selected-items">{{ filters.productIds.length }}
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.products" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllProducts"
                                                    :checked="filters.productIds.length === uniqueProducts.length">
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="p in uniqueProducts" :key="p.id">
                                                <input type="checkbox" :value="p.id" v-model="filters.productIds"
                                                    @change="applyFilters">
                                                <span>{{ p.name }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Columns Multi-Select -->
                            <div class="search-group">
                                <label class="search-label">
                                    Visible Columns
                                    <span class="multi-badge orange-badge">({{ selectedColumns.length }}/{{
                                        availableColumns.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display columns-select" @click="toggleDropdown('columns')">
                                        <span v-if="selectedColumns.length === availableColumns.length"
                                            class="selected-items">All columns selected</span>
                                        <span v-else class="selected-items">{{ selectedColumns.length }} columns
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.columns" class="multi-select-dropdown columns-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllColumns"
                                                    :checked="selectedColumns.length === availableColumns.length">
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="column in availableColumns"
                                                :key="column.key">
                                                <input type="checkbox" :value="column.key" v-model="selectedColumns"
                                                    @change="handleColumnToggle">
                                                <span>{{ column.label }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="filter-actions">
                            <button class="apply-filters-btn" @click="applyFilters">Apply Filters</button>
                            <button class="reset-filters-btn" @click="resetFilters">Reset</button>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Data Grid -->
            <div class="grid-container">
                <div class="grid-header">
                    <div class="grid-title">
                        <span class="report-name">{{ getReportTitle() }}</span>
                        <span class="date-range-label">Date: {{ formatDateForDisplay(filters.fromDate) }} - {{
                            formatDateForDisplay(filters.toDate) }}</span>
                    </div>
                    <div class="grid-controls">
                        <button v-if="filters.reportType === 'general' && hasActiveColumnSearches"
                            class="clear-column-searches-btn" @click="clearColumnSearches">
                            <span class="action-icon">✖</span><span>Clear Searches</span>
                        </button>
                        <div class="rows-selector">
                            <span class="rows-label">Show:</span>
                            <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                                <option :value="10">10 rows</option>
                                <option :value="25">25 rows</option>
                                <option :value="50">50 rows</option>
                                <option :value="100">100 rows</option>
                                <option :value="200">200 rows</option>
                            </select>
                        </div>
                        <div class="grid-actions">
                            <button class="action-button popout-btn" @click="popOutTable"><span
                                    class="action-icon">🔲</span><span>Pop Out</span></button>
                            <button class="action-button refresh-btn" @click="refreshData"><span
                                    class="action-icon">🔄</span><span>Refresh</span></button>
                            <button class="action-button export-btn" @click="exportData"><span
                                    class="action-icon">📥</span><span>Export</span></button>
                        </div>
                    </div>
                </div>

                <div class="table-container">
                    <div v-if="loading" class="loading-overlay">
                        <div class="loader"></div>
                    </div>

                    <table v-if="!loading && displayData.length > 0" class="data-table">
                        <thead>
                            <tr>
                                <th v-for="column in visibleColumns" :key="column.key">{{ column.label }}</th>
                                <th><span>ACTION</span></th>
                            </tr>
                            <tr v-if="filters.reportType === 'general'" class="search-row">
                                <th v-for="column in visibleColumns" :key="'s-' + column.key" class="search-cell">
                                    <input type="text" class="column-search-input" placeholder="Search..."
                                        v-model="columnSearchFilters[column.key]" @input="handleColumnSearch" />
                                </th>
                                <th>

                                </th>

                            </tr>
                            
                        </thead>
                        <tbody>
                            <tr v-for="(record, index) in paginatedRecords" :key="record.ID || record.groupKey || index"
                                @click="showRecordDetails(record)" :class="getRowClass(record)">
                                <td v-for="column in visibleColumns" :key="column.key">

                                    <div v-if="column.key === 'RequestDate'" class="date-time-cell">
                                        <div class="date-part">{{ formatDateTime(record.RequestDate) }}</div>
                                        <div class="time-ago">{{ getTimeAgo(record.RequestDate) }}</div>
                                    </div>
                                    <span v-if="column.key === 'ResponseDate'" class="date-cell">{{
                                        formatDateTime(record.ResponseDate) }}</span>
                                    <code v-if="column.key === 'OrderId'"
                                        class="transaction-id colorful">{{ record.OrderId }}</code>
                                    <code v-if="column.key === 'Store'"
                                        class="transaction-id colorful">{{ record.Store }}</code>
                                    <code v-if="column.key === 'ID'" class="id-code">{{ record.ID }}</code>
                                    <code v-if="column.key === 'TransactionGUID'"
                                        class="tag-code">{{ record.TransactionGUID }}</code>
                                    <code v-if="column.key === 'VehicleRegistration'"
                                        class="registration-badge">{{ record.VehicleRegistration }}</code>
                                    <span v-if="column.key === 'Driver'" class="personnel-name">{{ record.DriverFName }}
                                        {{ record.DriverLName }}</span>
                                    <span v-if="column.key === 'ProductName'"
                                        :class="['product-badge-new', getProductClass(record.ProductName)]">{{
                                        record.ProductName }}</span>
                                    <div v-if="column.key === 'QuantityOrdered'" class="volume-cell-new">
                                        <span class="volume-value">{{ formatVolume(record.QuantityOrdered)
                                            }}</span><span class="volume-unit">L</span>
                                    </div>
                                    <div v-if="column.key === 'QuantityDispensed'" class="volume-cell-new">
                                        <span class="volume-value">{{ formatVolume(record.QuantityDispensed)
                                            }}</span><span class="volume-unit">L</span>
                                    </div>
                                    <span v-if="column.key === 'TransactionID'" class="personnel-name">{{ record.TransactionID }}
                                        </span>
                                    <span v-if="column.key === 'VehicleMake'" class="equipment-name">{{
                                        record.VehicleMake || 'N/A' }}</span>
                                    <span v-if="column.key === 'VehicleModel'" class="equipment-name">{{
                                        record.VehicleModel || 'N/A' }}</span>
                                    <span v-if="column.key === 'VehicleOdometerReading'" class="metric-value">{{
                                        record.VehicleOdometerReading || 0 }}</span>
                                    <span v-if="column.key === 'StatusCode'"
                                        :class="['status-pill', getStatusClass(record.StatusCode)]">{{ record.StatusCode
                                        || 'N/A' }}</span>
                                    <div v-if="column.key === 'Success'" class="status-cell-new">
                                        <span
                                            :class="['status-dot-new', record.Success === 'Yes' ? 'processed' : 'failed']"></span>
                                        <span :class="record.Success === 'Yes' ? 'status-text-new' : 'text-failed'">{{
                                            record.Success }}</span>
                                    </div>
                                    <span v-if="column.key === 'ErrorMessage'" class="error-text"
                                        :title="record.ErrorMessage">
                                        {{ record.ErrorMessage ? (record.ErrorMessage.length > 45 ?
                                            record.ErrorMessage.substring(0, 45) + '…' : record.ErrorMessage) : '—' }}
                                    </span>

                                    <!-- Driver License -->
                                    <code v-if="column.key === 'DriverLicense'"
                                        class="tag-code">{{ record.DriverLicense || '—' }}</code>

                                    <!-- Product ID -->
                                    <code v-if="column.key === 'ProductId'"
                                        class="id-code">{{ record.ProductId || '—' }}</code>

                                    <!-- Store ID -->
                                    <code v-if="column.key === 'StoreID'"
                                        class="id-code">{{ record.StoreID || '—' }}</code>

                                    <!-- Aggregated columns -->
                                    <span v-if="column.key === 'groupName' && filters.reportType === 'usage-vehicle'"
                                        class="registration-badge">{{ record.groupName }}</span>
                                    <span v-if="column.key === 'groupName' && filters.reportType === 'usage-product'"
                                        :class="['product-badge-new', getProductClass(record.groupName)]">{{
                                        record.groupName }}</span>
                                    <span v-if="column.key === 'groupName' && filters.reportType === 'usage-driver'"
                                        class="personnel-name" style="font-weight:600">{{ record.groupName }}</span>
                                    <span v-if="column.key === 'groupName' && filters.reportType === 'usage-status'"
                                        :class="['status-pill', getStatusClass(record.groupName)]">{{ record.groupName
                                        }}</span>
                                    <span v-if="column.key === 'groupName' && filters.reportType === 'usage-make'"
                                        class="equipment-name" style="font-weight:600">{{ record.groupName }}</span>
                                    <span v-if="column.key === 'transactionCount'" class="count-value">{{
                                        record.transactionCount }}</span>
                                    <div v-if="column.key === 'totalDispensed'" class="volume-cell-new">
                                        <span class="volume-value">{{ formatVolume(record.totalDispensed) }}</span><span
                                            class="volume-unit">L</span>
                                    </div>
                                    <div v-if="column.key === 'avgDispensed'" class="volume-primary">{{
                                        formatVolume(record.avgDispensed) }} L</div>
                                    <span v-if="column.key === 'successCount'" class="status-text-new">{{
                                        record.successCount }}</span>
                                    <span v-if="column.key === 'failedCount'" class="text-failed">{{ record.failedCount
                                        }}</span>
                                   
                                </td>
                                <td>
                                     <span class = "manual-process">
                                        <button class="manual-process-btn" @click = "manualProcess(record)" v-if="record.StatusCode === 200" :disabled="record.StatusCode === 200">
                                            Manual process
                                        </button>
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="!loading && displayData.length === 0" class="empty-state">
                        <div class="empty-icon">⛽</div>
                        <div class="empty-title">No Records Found</div>
                        <div class="empty-description">Try adjusting your filters or check back later for new data</div>
                    </div>
                </div>

                <div v-if="totalPages > 1" class="pagination-container">
                    <div class="pagination-info">
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                        {{ Math.min(currentPage * itemsPerPage, displayData.length) }}
                        of {{ displayData.length }} entries
                    </div>
                    <div class="pagination-controls">
                        <button class="page-btn" @click="currentPage = 1" :disabled="currentPage === 1">First</button>
                        <button class="page-btn" @click="currentPage--" :disabled="currentPage === 1">Previous</button>
                        <button v-for="page in visiblePageNumbers" :key="page" class="page-btn page-number"
                            :class="{ 'active': page === currentPage }" @click="currentPage = page">{{ page }}</button>
                        <button class="page-btn" @click="currentPage++"
                            :disabled="currentPage === totalPages">Next</button>
                        <button class="page-btn" @click="currentPage = totalPages"
                            :disabled="currentPage === totalPages">Last</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'UsageDispensingShiptech',

    data: function () {
        return {
            fromDate : "",
            toDate : "",
            AccountID:localStorage.getItem('userAccountId'),
            selectedRecord: null,
            showDrillDown: false,
            drillDownTitle: '',
            drillDownGroup: '',
            drillDownData: { groupName: '', records: [], totalDispensed: 0, successCount: 0 },
            drillDownPosition: { x: 0, y: 0 },
            isDragging: false,
            dragStart: { x: 0, y: 0 },
            autoRefreshEnabled: false,
            refreshInterval: 60000,
            refreshTimer: null,
            _onDragBound: null,
            _stopDragBound: null,
            _handleClickOutsideBound: null,
            loading: false,
            apiConfig: { accountId: localStorage.getItem('userAccountId') },
            currentPage: 1,
            itemsPerPage: 25,
            records: [],
            summaryStats: { totalTransactions: 0, totalDispensed: 0, successCount: 0, failedCount: 0, uniqueVehicles: 0, avgDispensed: 0, successRate: 0 },
            filters: {
                fromDate: this.getDefaultFromDate(),
                toDate: this.getDefaultToDate(),
                reportType: 'general',
                successFilter: '',
                vehicleIds: [],
                productIds: []
            },
            filtersVisible: true,
            statsVisible: true,
            dropdownStates: { columns: false, vehicles: false, products: false },
            uniqueVehicles: [],
            uniqueProducts: [],
            availableColumns: [],
            selectedColumns: [],
            columnSearchFilters: {},
            allColumns: {
                general: [
                    { key: 'RequestDate', label: 'Request Date', order: 1 },
                    { key: 'OrderId', label: 'Order ID', order: 2 },
                    { key: 'ID', label: 'ID', order: 3 },
                    { key: 'TransactionGUID', label: 'GUID', order: 4 },
                    { key: 'Store', label: 'Store', order: 5 },
                    { key: 'VehicleRegistration', label: 'Registration', order: 6 },
                    { key: 'Driver', label: 'Driver', order: 7 },
                    { key: 'DriverLicense', label: 'Driver License', order: 8 },
                    { key: 'ProductName', label: 'Product', order: 9 },
                    { key: 'ProductId', label: 'Product ID', order: 10 },
                    { key: 'QuantityOrdered', label: 'Qty Ordered', order: 11 },
                    { key: 'QuantityDispensed', label: 'Qty Dispensed', order: 12 },
                    { key: 'VehicleMake', label: 'Vehicle Make', order: 13 },
                    { key: 'VehicleModel', label: 'Vehicle Model', order: 14 },
                    { key: 'VehicleOdometerReading', label: 'Odometer', order: 15 },
                    { key: 'StatusCode', label: 'Status Code', order: 16 },
                    { key: 'Success', label: 'Success', order: 17 },
                    { key: 'ErrorMessage', label: 'Error Message', order: 18 },
                    { key: 'ResponseDate', label: 'Response Date', order: 19 },
                    { key: 'TransactionID', label: 'Transaction ID', order: 20 }
                ],
                aggregated: [
                    { key: 'groupName', label: 'Name', order: 1 },
                    { key: 'transactionCount', label: 'Transactions', order: 2 },
                    { key: 'totalDispensed', label: 'Total Dispensed', order: 3 },
                    { key: 'avgDispensed', label: 'Avg Dispensed', order: 4 },
                    { key: 'successCount', label: 'Successful', order: 5 },
                    { key: 'failedCount', label: 'Failed', order: 6 }
                ]
            }
        };
    },

    computed: {
        visibleColumns: function () {
            var self = this;
            return this.availableColumns
                .filter(function (col) { return self.selectedColumns.indexOf(col.key) !== -1; })
                .sort(function (a, b) { return a.order - b.order; });
        },
        hasActiveColumnSearches: function () {
            var self = this;
            return Object.keys(this.columnSearchFilters).some(function (k) {
                return self.columnSearchFilters[k] && self.columnSearchFilters[k].trim() !== '';
            });
        },
        filteredRecords: function () {
            var filtered = this.records.slice();
            var self = this;
            // Client-side date filtering (controller returns all records without date params)
            if (this.filters.fromDate) {
                var fromMs = new Date(this.filters.fromDate).setHours(0, 0, 0, 0);
                filtered = filtered.filter(function (r) { return r.RequestDate && new Date(r.RequestDate).getTime() >= fromMs; });
            }
            if (this.filters.toDate) {
                var toMs = new Date(this.filters.toDate).setHours(23, 59, 59, 999);
                filtered = filtered.filter(function (r) { return r.RequestDate && new Date(r.RequestDate).getTime() <= toMs; });
            }
            if (this.filters.successFilter) {
                filtered = filtered.filter(function (r) { return r.Success === self.filters.successFilter; });
            }
            if (this.filters.vehicleIds && this.filters.vehicleIds.length > 0) {
                filtered = filtered.filter(function (r) { return self.filters.vehicleIds.indexOf(r.VehicleRegistration) !== -1; });
            }
            if (this.filters.productIds && this.filters.productIds.length > 0) {
                filtered = filtered.filter(function (r) { return self.filters.productIds.indexOf(r.ProductName) !== -1; });
            }
            if (this.columnSearchFilters && Object.keys(this.columnSearchFilters).length > 0) {
                filtered = filtered.filter(function (record) {
                    for (var key in self.columnSearchFilters) {
                        var sv = self.columnSearchFilters[key];
                        if (!sv || !sv.trim()) continue;
                        var search = sv.toLowerCase();
                        var val = key === 'Driver'
                            ? ((record.DriverFName || '') + ' ' + (record.DriverLName || '')).toLowerCase()
                            : String(record[key] || '').toLowerCase();
                        if (!val.includes(search)) return false;
                    }
                    return true;
                });
            }
            return filtered;
        },
        displayData: function () {
            if (this.filters.reportType === 'general') return this.filteredRecords;
            return this.aggregateData();
        },
        totalPages: function () { return Math.ceil(this.displayData.length / this.itemsPerPage); },
        paginatedRecords: function () {
            var start = (this.currentPage - 1) * this.itemsPerPage;
            return this.displayData.slice(start, start + this.itemsPerPage);
        },
        visiblePageNumbers: function () {
            var maxV = 5, pages = [];
            var start = Math.max(1, this.currentPage - 2);
            var end = Math.min(this.totalPages, start + maxV - 1);
            if (end - start < maxV - 1) start = Math.max(1, end - maxV + 1);
            for (var i = start; i <= end; i++) pages.push(i);
            return pages;
        }
    },

    methods: {
        dateFormat(date) {
            var month = '';
            var year = '';
            var day = '';

            var d = new Date(date),
                month = '' + (d.getMonth() + 1),
                day = '' + d.getDate(),
                year = d.getFullYear();
            if (month.length < 2)
                month = '0' + month;
            if (day.length < 2)
                day = '0' + day;
            return [year, month, day].join('-');
            },

        getDefaultFromDate: function () {
            var d = new Date(); d.setDate(1);
            return this.formatDateForInput(d);
        },
        getDefaultToDate: function () { return this.formatDateForInput(new Date()); },
        formatDateForInput: function (date) {
            return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
        },

        manualProcess(dr) {
            
            //Empty method for manual process
        },

        loadData: function () {
            var self = this;
            self.loading = true;
             var date = new Date();
            this.filters.fromDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
            this.filters.toDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
            //var url = 'IntegrationShiptech/UsageDispensing?AccountID=321&fromDate=2026-03-10&toDate=2026-03-11';
            var accountId = localStorage.getItem("userAccountId").toString();
            
            var url = `IntegrationShiptech/UsageDispensing?AccountID=${accountId}&fromDate=${this.filters.fromDate}&toDate=${this.filters.toDate}`;
            
            // AccountID
            // self.fromDate
            // self.toDate 
            // self.AccountID
            
//   let details = `&clientKey=${localStorage.getItem('userKey')}&accountkey=${localStorage.getItem('userAccountKey')}&fromdate=${self.dateFormat(self.formData.fromDate)}&todate=${self.dateFormat(self.formData.toDate)}`
//    
//     &accountkey=${localStorage.getItem('userAccountKey')}&ID=${_Id1}&ID2=${_Id2}
//       &fromdate=${self.dateFormat(self.formData.fromDate)}&todate=${self.dateFormat(self.formData.toDate)}`;



            var onSuccess = function (response) {
                //debugger;
                if (response.data && Array.isArray(response.data)) {
                    self.records = response.data.map(function (field) {
                        // Parse productDispensed JSON: {"ProductId","QuantityOrdered","QuantityDispensed","ProductName"}
                        var productInfo = {};
                        try { productInfo = typeof field.productDispensed === 'string' ? JSON.parse(field.productDispensed) : (field.productDispensed || {}); } catch (e) { }

                        // Parse fields JSON array: [{Key, Value}] — also keep raw array for table display
                        var fieldsMap = {};
                        var fieldsArray = [];
                        try {
                            var arr = typeof field.fields === 'string' ? JSON.parse(field.fields) : (field.fields || []);
                            if (Array.isArray(arr)) {
                                fieldsArray = arr;
                                arr.forEach(function (f) { fieldsMap[f.Key || f.key] = f.Value || f.value; });
                            }
                        } catch (e) { }

                        return {
                            // Root-level columns (new camelCase names from controller)
                            ID: field.id,
                            AccountID: field.accountId,
                            Store: field.store,
                            TransactionGUID: field.token,
                            OrderId: field.orderId,
                            TransactionID: field.transactionID,
                            RequestDate: field.dateDispensed || null,
                            ResponseDate: field.dateCompleted || null,
                            ErrorMessage: (field.error && field.error !== 'N/A') ? field.error : null,
                            StatusCode: (field.httpCode && field.httpCode !== 'N/A') ? field.httpCode : null,
                            // isProcessed: 0 = not done / failed, 1 = processed / success
                            Success: field.isProcessed === 1 ? 'Yes' : 'No',
                            ResponseBody: (field.messageApi && field.messageApi !== 'N/A') ? field.messageApi : null,

                            // From productDispensed JSON
                            ProductId: productInfo.ProductId || null,
                            ProductName: productInfo.ProductName || null,
                            QuantityOrdered: parseFloat(productInfo.QuantityOrdered) || 0,
                            QuantityDispensed: parseFloat(productInfo.QuantityDispensed) || 0,

                            // From fields JSON array: well-known keys
                            DriverFName: fieldsMap['DriverFName'] || '',
                            DriverLName: fieldsMap['DriverLName'] || '',
                            VehicleRegistration: fieldsMap['VehicleRegistration'] || null,
                            VehicleOdometerReading: fieldsMap['VehicleOdometerReading'] ? parseFloat(fieldsMap['VehicleOdometerReading']) : null,
                            VehicleMake: fieldsMap['VehicleMake'] || null,
                            VehicleModel: fieldsMap['VehicleModel'] || null,
                            DriverLicense: fieldsMap['DriverLicense'] || null,

                            // Raw fields array kept for dynamic table display in detail popup
                            fieldsArray: fieldsArray
                        };
                    });
                    self.calculateSummaryStats();
                    self.extractUniqueValues();
                    self.updateAvailableColumns();
                    self.showNotification('Success', 'Loaded ' + self.records.length + ' records', 'success');
                } else { self.records = []; }
            };

            var onFinally = function () { self.loading = false; };
            self.$ajaxGet(self, url, onSuccess, onFinally);
        },

        calculateSummaryStats: function () {
            var vehicles = new Set(), totalDispensed = 0, successCount = 0;
            this.records.forEach(function (r) {
                totalDispensed += r.QuantityDispensed || 0;
                if (r.Success === 'Yes') successCount++;
                if (r.VehicleRegistration) vehicles.add(r.VehicleRegistration);
            });
            var total = this.records.length;
            this.summaryStats = {
                totalTransactions: total,
                totalDispensed: totalDispensed,
                successCount: successCount,
                failedCount: total - successCount,
                uniqueVehicles: vehicles.size,
                avgDispensed: total > 0 ? totalDispensed / total : 0,
                successRate: total > 0 ? Math.round((successCount / total) * 100) : 0
            };
        },

        extractUniqueValues: function () {
            var vehicles = {}, products = {};
            this.records.forEach(function (r) {
                if (r.VehicleRegistration) vehicles[r.VehicleRegistration] = r.VehicleRegistration;
                if (r.ProductName) products[r.ProductName] = r.ProductName;
            });
            this.uniqueVehicles = Object.keys(vehicles).map(function (k) { return { id: k, name: k }; }).sort(function (a, b) { return a.name.localeCompare(b.name); });
            this.uniqueProducts = Object.keys(products).map(function (k) { return { id: k, name: k }; }).sort(function (a, b) { return a.name.localeCompare(b.name); });
        },

        updateAvailableColumns: function () {
            if (this.filters.reportType === 'general') {
                this.availableColumns = this.allColumns.general.slice();
                this.selectedColumns = ['RequestDate', 'OrderId', 'Store', 'VehicleRegistration', 'Driver', 'ProductName', 'QuantityOrdered', 'QuantityDispensed', 'StatusCode', 'Success', 'ErrorMessage', 'TransactionID'];
            } else {
                this.availableColumns = this.allColumns.aggregated.slice();
                this.selectedColumns = this.allColumns.aggregated.map(function (c) { return c.key; });
            }
        },

        aggregateData: function () {
            var agg = {}, rt = this.filters.reportType;
            this.filteredRecords.forEach(function (r) {
                var key = '', name = '';
                switch (rt) {
                    case 'usage-vehicle': key = r.VehicleRegistration || 'unknown'; name = r.VehicleRegistration || 'Unknown'; break;
                    case 'usage-product': key = r.ProductName || 'unknown'; name = r.ProductName || 'Unknown'; break;
                    case 'usage-driver': key = (r.DriverFName + '_' + r.DriverLName); name = (r.DriverFName + ' ' + r.DriverLName).trim() || 'Unknown'; break;
                    case 'usage-status': key = r.StatusCode || 'none'; name = r.StatusCode || 'No Status'; break;
                    case 'usage-make': key = r.VehicleMake || 'unknown'; name = r.VehicleMake || 'Unknown'; break;
                    default: key = 'all'; name = 'All';
                }
                if (!agg[key]) agg[key] = { groupKey: key, groupName: name, transactionCount: 0, totalDispensed: 0, successCount: 0, failedCount: 0, records: [] };
                agg[key].transactionCount++;
                agg[key].totalDispensed += r.QuantityDispensed || 0;
                if (r.Success === 'Yes') agg[key].successCount++;
                else agg[key].failedCount++;
                agg[key].records.push(r);
            });
            return Object.values(agg).map(function (g) {
                g.avgDispensed = g.transactionCount > 0 ? g.totalDispensed / g.transactionCount : 0;
                return g;
            }).sort(function (a, b) { return b.totalDispensed - a.totalDispensed; });
        },

        getReportTitle: function () {
            var titles = { 'general': 'API Data — General View', 'usage-vehicle': 'Usage per Vehicle', 'usage-product': 'Usage per Product', 'usage-driver': 'Usage per Driver', 'usage-status': 'Usage per Status Code', 'usage-make': 'Usage per Vehicle Make' };
            return titles[this.filters.reportType] || 'Dispensing API data';
        },

        handleReportTypeChange: function () { this.updateAvailableColumns(); this.currentPage = 1; this.columnSearchFilters = {}; },
        handleColumnSearch: function () { this.currentPage = 1; },

        getProductClass: function (product) {
            if (!product) return 'product-other';
            var p = product.toLowerCase();
            if (p.includes('diesel')) return 'product-diesel';
            if (p.includes('petrol')) return 'product-petrol';
            if (p.includes('gas')) return 'product-gas';
            return 'product-other';
        },

        getStatusClass: function (code) {
            var c = String(code || '');
            if (c === '200') return 'status-200';
            if (c === '401') return 'status-401';
            if (c === '400') return 'status-400';
            if (c === '422') return 'status-422';
            return 'status-default';
        },

        getRowClass: function (record) {
            if (record.Success === 'Yes') return 'row-success';
            if (record.Success === 'No') return 'row-failed';
            return '';
        },

        showRecordDetails: function (record) {
            if (record.groupKey !== undefined) {
                this.drillDownTitle = 'Drill-down: ' + record.groupName;
                this.drillDownGroup = this.filters.reportType.replace('usage-', '').replace('-', ' ');
                this.drillDownData = { groupName: record.groupName, records: record.records || [], totalDispensed: record.totalDispensed || 0, successCount: record.successCount || 0 };
                this.drillDownPosition = { x: Math.max(0, (window.innerWidth - 1200) / 2), y: 50 };
                this.showDrillDown = true;
            } else {
                this.selectedRecord = record;
            }
        },

        closeDrillDown: function () { this.showDrillDown = false; this.isDragging = false; },

        startDrag: function (e) {
            var self = this;
            this.isDragging = true;
            var cx = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
            var cy = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
            this.dragStart = { x: cx - this.drillDownPosition.x, y: cy - this.drillDownPosition.y };
            
            self._onDragBound = function (e) {
                if (!self.isDragging) return;
                self.drillDownPosition = { x: Math.max(0, Math.min(e.clientX - self.dragStart.x, window.innerWidth - 1200)), y: Math.max(0, Math.min(e.clientY - self.dragStart.y, window.innerHeight - 600)) };
            };
            
            self._stopDragBound = function () {
                self.isDragging = false;
                document.removeEventListener('mousemove', self._onDragBound);
                document.removeEventListener('mouseup', self._stopDragBound);
            };
            
            document.addEventListener('mousemove', self._onDragBound);
            document.addEventListener('mouseup', self._stopDragBound);
            e.preventDefault();
        },
        onDrag: function (e) {
            if (!this.isDragging) return;
            this.drillDownPosition = { x: Math.max(0, Math.min(e.clientX - this.dragStart.x, window.innerWidth - 1200)), y: Math.max(0, Math.min(e.clientY - this.dragStart.y, window.innerHeight - 600)) };
        },
        stopDrag: function () { 
            this.isDragging = false; 
            if (this._onDragBound) {
                document.removeEventListener('mousemove', this._onDragBound);
            }
            if (this._stopDragBound) {
                document.removeEventListener('mouseup', this._stopDragBound);
            }
        },

        exportDrillDownCSV: function () {
            var self = this, csv = 'Request Date,Registration,Driver,Product,Ordered,Dispensed,Status,Error\n';
            this.drillDownData.records.forEach(function (r) {
                csv += [self.formatDateTime(r.RequestDate), r.VehicleRegistration || '', r.DriverFName + ' ' + r.DriverLName, r.ProductName || '', self.formatVolume(r.QuantityOrdered), self.formatVolume(r.QuantityDispensed), r.StatusCode || '', r.ErrorMessage || ''].map(function (v) { return '"' + v + '"'; }).join(',') + '\n';
            });
            var a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
            a.download = 'dispensing_' + this.drillDownData.groupName.replace(/\s+/g, '_') + '.csv';
            a.click();
        },

        printDrillDown: function () {
            var self = this, w = window.open('', '_blank', 'width=900,height=600,scrollbars=yes');
            var html = '<!DOCTYPE html><html><head><title>' + this.drillDownTitle + '</title><style>body{font-family:Arial,sans-serif;margin:20px}h1{color:#D2691E}table{width:100%;border-collapse:collapse}th{background:#D2691E;color:#fff;padding:10px;text-align:left}td{padding:8px;border-bottom:1px solid #eee}</style></head><body><h1>' + this.drillDownData.groupName + '</h1><p>Total Dispensed: <b>' + this.formatVolume(this.drillDownData.totalDispensed) + ' L</b></p><table><thead><tr><th>Date</th><th>Registration</th><th>Driver</th><th>Product</th><th>Ordered</th><th>Dispensed</th><th>Status</th><th>Error</th></tr></thead><tbody>';
            this.drillDownData.records.forEach(function (r) { html += '<tr><td>' + self.formatDateTime(r.RequestDate) + '</td><td>' + r.VehicleRegistration + '</td><td>' + r.DriverFName + ' ' + r.DriverLName + '</td><td>' + (r.ProductName || '') + '</td><td>' + self.formatVolume(r.QuantityOrdered) + ' L</td><td>' + self.formatVolume(r.QuantityDispensed) + ' L</td><td>' + (r.StatusCode || '') + '</td><td>' + (r.ErrorMessage || '—') + '</td></tr>'; });
            html += '</tbody></table></body></html>';
            w.document.write(html); w.document.close(); w.print();
        },

        clearAllFilters: function () { this.filters.vehicleIds = []; this.filters.productIds = []; this.filters.successFilter = ''; this.currentPage = 1; },

        toggleDropdown: function (type) {
            var self = this;
            Object.keys(this.dropdownStates).forEach(function (k) { if (k !== type) self.dropdownStates[k] = false; });
            this.dropdownStates[type] = !this.dropdownStates[type];
        },

        toggleAllVehicles: function () {
            this.filters.vehicleIds = this.filters.vehicleIds.length === this.uniqueVehicles.length ? [] : this.uniqueVehicles.map(function (v) { return v.id; });
            this.applyFilters();
        },
        toggleAllProducts: function () {
            this.filters.productIds = this.filters.productIds.length === this.uniqueProducts.length ? [] : this.uniqueProducts.map(function (p) { return p.id; });
            this.applyFilters();
        },
        toggleAllColumns: function () {
            this.selectedColumns = this.selectedColumns.length === this.availableColumns.length ? [this.availableColumns[0].key] : this.availableColumns.map(function (c) { return c.key; });
        },
        handleColumnToggle: function () { if (this.selectedColumns.length === 0) this.selectedColumns = [this.availableColumns[0].key]; },

        applyFilters: function () { this.currentPage = 1; },

        resetFilters: function () {
            this.filters = { fromDate: this.getDefaultFromDate(), toDate: this.getDefaultToDate(), reportType: 'general', successFilter: '', vehicleIds: [], productIds: [] };
            this.columnSearchFilters = {};
            this.updateAvailableColumns();
            this.currentPage = 1;
            this.loadData();
            this.showNotification('Success', 'Filters reset', 'success');
        },

        clearColumnSearches: function () { this.columnSearchFilters = {}; this.currentPage = 1; },
        refreshData: function () { this.showNotification('Info', 'Refreshing data...', 'primary'); this.loadData(); },

        exportData: function () {
            var self = this;
            var headers = this.visibleColumns.map(function (c) { return c.label; });
            var rows = this.displayData.map(function (record) {
                return self.visibleColumns.map(function (col) {
                    if (col.key === 'RequestDate' || col.key === 'ResponseDate') return '"' + self.formatDateTime(record[col.key]) + '"';
                    if (col.key === 'Driver') return '"' + (record.DriverFName + ' ' + record.DriverLName).trim() + '"';
                    return '"' + (record[col.key] != null ? record[col.key] : '') + '"';
                }).join(',');
            });
            var a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([[headers.join(',')].concat(rows).join('\n')], { type: 'text/csv' }));
            a.download = 'dispensing_' + this.filters.reportType + '_' + Date.now() + '.csv';
            a.click();
            this.showNotification('Success', 'Data exported', 'success');
        },

        popOutTable: function () {
            var self = this, w = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
            if (!w) { this.showNotification('Error', 'Allow popups for this site', 'danger'); return; }
            var html = '<!DOCTYPE html><html><head><title>' + this.getReportTitle() + '</title><style>body{font-family:Arial,sans-serif;margin:20px}h1{color:#D2691E;font-size:1.5rem;margin-bottom:20px}table{width:100%;border-collapse:collapse}th{background:#D2691E;color:#fff;padding:12px;text-align:left;position:sticky;top:0}td{padding:10px;border-bottom:1px solid #e0e0e0}tr:hover{background:#f5f5f5}</style></head><body><h1>' + this.getReportTitle() + '</h1><p>' + this.filters.fromDate + ' to ' + this.filters.toDate + ' — ' + this.displayData.length + ' records</p><table><thead><tr>' + this.visibleColumns.map(function (c) { return '<th>' + c.label + '</th>'; }).join('') + '</tr></thead><tbody>';
            this.displayData.forEach(function (r) {
                html += '<tr>' + self.visibleColumns.map(function (col) {
                    if (col.key === 'RequestDate' || col.key === 'ResponseDate') return '<td>' + self.formatDateTime(r[col.key]) + '</td>';
                    if (col.key === 'Driver') return '<td>' + r.DriverFName + ' ' + r.DriverLName + '</td>';
                    return '<td>' + (r[col.key] != null ? r[col.key] : '') + '</td>';
                }).join('') + '</tr>';
            });
            html += '</tbody></table></body></html>';
            w.document.write(html); w.document.close();
            this.showNotification('Success', 'Opened in new window', 'success');
        },

        handleRowsChange: function () { this.currentPage = 1; },
        handleAutoRefreshToggle: function () {
            if (this.autoRefreshEnabled) { this.startAutoRefresh(); this.showNotification('Success', 'Auto-refresh enabled', 'success'); }
            else { this.stopAutoRefresh(); this.showNotification('Info', 'Auto-refresh disabled', 'primary'); }
        },
        handleRefreshIntervalChange: function () { if (this.autoRefreshEnabled) { this.stopAutoRefresh(); this.startAutoRefresh(); } },
        startAutoRefresh: function () { var self = this; this.stopAutoRefresh(); if (this.autoRefreshEnabled) this.refreshTimer = setInterval(function () { self.loadData(); }, this.refreshInterval); },
        stopAutoRefresh: function () { if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; } },
        formatRefreshInterval: function () { var s = this.refreshInterval / 1000; return s < 60 ? s + 's' : (s / 60) + 'm'; },
        formatVolume: function (v) { return v != null ? parseFloat(v).toFixed(2) : '0.00'; },
        formatDateTime: function (date) {
            if (!date) return 'N/A';
            var d = new Date(date);
            return d.getFullYear() + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0') + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
        },
        formatDateForDisplay: function (date) {
            if (!date) return 'N/A';
            var d = new Date(date);
            return d.getFullYear() + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0');
        },
        getTimeAgo: function (date) {
            if (!date) return '';
            var diff = Math.floor((new Date() - new Date(date)) / 1000);
            if (diff < 60) return diff + 's ago';
            if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
            if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
            return Math.floor(diff / 86400) + 'd ago';
        },
        showNotification: function (title, text, type) {
            if (this.$vs && this.$vs.notify) this.$vs.notify({ title: title, text: text, color: type, position: 'top-right' });
        },
        handleClickOutside: function (event) {
            if (!this.$el) return;
            var inside = false;
            var self = this;
            this.$el.querySelectorAll('.multi-select-container').forEach(function (c) { if (c.contains(event.target)) inside = true; });
            if (!inside) { 
                Object.keys(this.dropdownStates).forEach(function (k) { 
                    self.dropdownStates[k] = false; 
                }); 
            }
        }
    },

    mounted: function () {
        var self = this;
        this.updateAvailableColumns();
        this.loadData();
        if (this.autoRefreshEnabled) this.startAutoRefresh();
        
        // Bind handleClickOutside with proper context
        this._handleClickOutsideBound = function (event) {
            self.handleClickOutside(event);
        };
        
        document.addEventListener('click', this._handleClickOutsideBound);
        this.showNotification('Welcome!', 'FAMS Dispensing API Monitor - Ready', 'success');
    },

    beforeDestroy: function () {
        this.stopAutoRefresh();
        if (this._handleClickOutsideBound) {
            document.removeEventListener('click', this._handleClickOutsideBound);
        }
        if (this._onDragBound) {
            document.removeEventListener('mousemove', this._onDragBound);
        }
        if (this._stopDragBound) {
            document.removeEventListener('mouseup', this._stopDragBound);
        }
    }
};
</script>

<style>
:root {
    --autumn-primary: #D2691E;
    --autumn-accent: #FF8C00;
}

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #e2e8f0;
    height: 100vh;
    overflow: hidden;
    width: 100%;
}

#app {
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-width: none;
}

.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.logo-icon {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: var(--autumn-primary);
}

.dashboard-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 600;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.loader-controls {
    display: flex;
    align-items: center;
    gap: 15px;
}

.toggle-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
}

.toggle-checkbox {
    width: 18px;
    height: 18px;
    margin-right: 8px;
    cursor: pointer;
    accent-color: white;
}

.toggle-text {
    color: white;
    font-size: .875rem;
    font-weight: 500;
}

.interval-label {
    display: flex;
    align-items: center;
    gap: 8px;
}

.interval-text {
    color: white;
    font-size: .875rem;
    font-weight: 500;
}

.interval-select {
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, .3);
    background: rgba(255, 255, 255, .2);
    color: white;
    font-size: .875rem;
    cursor: pointer;
}

.interval-select option {
    background: #FF8C00;
    color: white;
}

.live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, .2);
    padding: 8px 16px;
    border-radius: 20px;
}

.live-dot {
    width: 8px;
    height: 8px;
    background: #4CAF50;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

.live-dot.auto-refresh {
    animation: pulse 1s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, .7)
    }

    70% {
        box-shadow: 0 0 0 10px rgba(76, 175, 80, 0)
    }

    100% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0)
    }
}

.header-refresh-btn {
    padding: 8px 16px;
    background: rgba(255, 255, 255, .2);
    border: 1px solid rgba(255, 255, 255, .3);
    border-radius: 6px;
    color: white;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: 12px;
}

.header-refresh-btn:hover {
    background: rgba(255, 255, 255, .3);
    transform: translateY(-1px);
}

.refresh-icon {
    display: inline-block;
    transition: transform .3s;
}

.header-refresh-btn:hover .refresh-icon {
    transform: rotate(180deg);
}

.main-content {
    flex: 1;
    overflow: auto;
    padding: 20px;
    width: 100%;
    background: #e2e8f0;
}

.stats-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.stats-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.stats-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.toggle-stats-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.toggle-stats-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: translateY(-1px);
}

.stats-wrapper {
    padding: 16px 20px;
}

.stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.stat-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    transition: all .3s;
    border: 1px solid #e2e8f0;
    position: relative;
    overflow: visible;
}

.clickable-stat {
    cursor: pointer;
}

.clickable-stat:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, .15);
    background: #fff;
}

.stat-hint {
    position: absolute;
    bottom: 8px;
    right: 8px;
    font-size: .625rem;
    color: #94a3b8;
    opacity: 0;
    transition: opacity .3s;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .5px;
}

.clickable-stat:hover .stat-hint {
    opacity: 1;
}

.total-stat {
    border-left: 4px solid #FF8C00;
}

.volume-stat {
    border-left: 4px solid #4CAF50;
}

.cost-stat {
    border-left: 4px solid #27AE60;
}

.equipment-stat {
    border-left: 4px solid #E74C3C;
}

.product-stat {
    border-left: 4px solid #2196F3;
}

.average-stat {
    border-left: 4px solid #9C27B0;
}

.stat-label {
    color: #2c3e50;
    font-size: .875rem;
    margin-bottom: 8px;
    font-weight: 600;
}

.stat-value {
    font-size: 1.75rem;
    color: #2c3e50;
    font-weight: bold;
}

.stat-change {
    font-size: .75rem;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.stat-change.positive {
    color: #27AE60;
}

.stat-change.negative {
    color: #E74C3C;
}

.search-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.filter-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.filter-header-buttons {
    display: flex;
    gap: 10px;
    align-items: center;
}

.toggle-filter-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.toggle-filter-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: translateY(-1px);
}

.reset-filter-header-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border: none;
    color: white;
    font-size: .9rem;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
}

.reset-filter-header-btn:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, .3);
}

.reset-icon {
    font-size: 1.1rem;
    font-weight: bold;
    display: inline-block;
    transition: transform .3s;
}

.reset-filter-header-btn:hover .reset-icon {
    transform: rotate(180deg);
}

.search-section.collapsed .filter-header {
    border-radius: 12px;
    border-bottom: none;
}

.search-controls-wrapper {
    padding: 16px 20px;
}

.search-controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.search-group {
    display: flex;
    flex-direction: column;
    position: relative;
}

.search-label {
    font-size: .875rem;
    color: #34495E;
    margin-bottom: 4px;
    font-weight: 500;
}

.search-input {
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    font-size: 14px;
    transition: all .3s;
    background: #fff;
}

.search-input:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 3px rgba(210, 105, 30, .15);
}

.multi-select-container {
    position: relative;
}

.multi-select-display {
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all .3s;
    min-height: 44px;
}

.multi-select-display:hover {
    border-color: #D2691E;
    background: #fff8f3;
}

.placeholder {
    color: #999;
}

.selected-items {
    color: #495057;
    font-weight: 500;
}

.dropdown-arrow {
    color: #999;
    font-size: .75rem;
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 2px solid #D2691E;
    border-radius: 8px;
    margin-top: 4px;
    max-height: 250px;
    overflow-y: auto;
    z-index: 9999;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.select-all-option {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    background: #f8f9fa;
}

.dropdown-options {
    max-height: 200px;
    overflow-y: auto;
}

.checkbox-option {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    transition: background .2s;
}

.checkbox-option:hover {
    background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
    margin-right: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #D2691E;
}

.multi-badge {
    background: #FF8C00;
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: .75rem;
    font-weight: 600;
    margin-left: 4px;
}

.orange-badge {
    background: #FF8C00;
}

.columns-select {
    border: 2px solid #FF8C00;
    background: #FFF8F3;
}

.columns-dropdown {
    max-height: 400px;
}

.filter-actions {
    display: flex;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}

.apply-filters-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all .3s;
}

.apply-filters-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(210, 105, 30, .3);
}

.reset-filters-btn {
    padding: 10px 24px;
    background: white;
    color: #34495E;
    border: 2px solid #FFE4B5;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all .3s;
}

.reset-filters-btn:hover {
    background: #FFF8F3;
    border-color: #FF8C00;
    transform: translateY(-1px);
}

.grid-container {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    overflow: hidden;
    border: 1px solid #cbd5e1;
}

.grid-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.grid-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.report-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #334155;
}

.date-range-label {
    font-size: .9rem;
    color: #64748b;
    font-weight: 500;
    font-style: italic;
}

.grid-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.rows-selector {
    display: flex;
    align-items: center;
    gap: 8px;
}

.rows-label {
    font-size: .875rem;
    color: #6c757d;
    font-weight: 500;
}

.rows-select {
    padding: 6px 12px;
    border: 1px solid #94a3b8;
    border-radius: 6px;
    background: white;
    color: #475569;
    font-size: .875rem;
    cursor: pointer;
    min-width: 100px;
}

.grid-actions {
    display: flex;
    gap: 8px;
}

.action-button {
    padding: 8px 16px;
    background: white;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    color: #475569;
    font-size: .875rem;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    white-space: nowrap;
}

.action-button:hover {
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.export-btn {
    color: #10B981;
    border-color: #D1FAE5;
}

.export-btn:hover {
    background: #ECFDF5;
    border-color: #10B981;
}

.popout-btn {
    color: #8B5CF6;
    border-color: #DDD6FE;
}

.popout-btn:hover {
    background: #F3E8FF;
    border-color: #8B5CF6;
}

.refresh-btn {
    color: #3B82F6;
    border-color: #DBEAFE;
}

.refresh-btn:hover {
    background: #EFF6FF;
    border-color: #3B82F6;
}

.clear-column-searches-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .2s;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-right: 10px;
}

.clear-column-searches-btn:hover {
    transform: translateY(-1px);
}

.table-container {
    overflow: auto;
    position: relative;
    max-height: 600px;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    z-index: 10;
}

.data-table th {
    padding: 12px 16px;
    text-align: left;
    color: white;
    font-weight: 600;
    font-size: .875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
    white-space: nowrap;
}

.data-table tbody tr {
    border-bottom: 1px solid #e2e8f0;
    transition: all .2s;
    background: #fff;
    cursor: pointer;
}

.data-table tbody tr:hover {
    background: #f1f5f9;
}

.data-table tbody tr.row-success {
    background: rgba(39, 174, 96, .03);
}

.data-table tbody tr.row-failed {
    background: rgba(231, 76, 60, .04);
    border-left: 3px solid #E74C3C;
}

.data-table td {
    padding: 12px 16px;
    font-size: .875rem;
    color: #334155;
}

.search-row {
    background: #fff3e0 !important;
}

.search-row th {
    background: transparent !important;
    padding: 6px !important;
    border-right: 1px solid #ffcc80 !important;
}

.column-search-input {
    width: 100%;
    padding: 5px 8px;
    border: 1px solid #ffcc80;
    border-radius: 4px;
    font-size: .75rem;
    background: white;
    color: #333;
    transition: all .2s;
    font-weight: normal;
    letter-spacing: normal;
    text-transform: none;
}

.column-search-input:focus {
    outline: none;
    border-color: #ff9800;
    box-shadow: 0 0 0 2px rgba(255, 152, 0, .2);
}

.column-search-input::placeholder {
    color: #999;
    font-style: italic;
}

.date-time-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.date-part {
    font-size: .875rem;
    color: #334155;
    font-weight: 500;
}

.time-ago {
    font-size: .75rem;
    color: #94a3b8;
    font-style: italic;
}

.date-cell {
    font-family: monospace;
    color: #475569;
}

.transaction-id {
    font-family: 'Courier New', monospace;
    font-size: .75rem;
}

.transaction-id.colorful {
    color: #ec4899;
    background: rgba(236, 72, 153, .1);
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
}

.id-code {
    font-family: monospace;
    font-size: .75rem;
    color: #64748b;
}

.registration-badge {
    display: inline-block;
    background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
    color: white;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: .8rem;
    font-weight: 600;
    font-family: monospace;
    letter-spacing: .5px;
}

.tag-code {
    background: #e2e8f0;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: .75rem;
    color: #334155;
    font-family: monospace;
}

.product-badge-new {
    display: inline-block;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: .8rem;
    font-weight: 600;
}

.product-diesel {
    background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
    color: white;
}

.product-petrol {
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
    color: white;
}

.product-gas {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    color: white;
}

.product-other {
    background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
    color: white;
}

.volume-cell-new {
    display: inline-flex;
    align-items: baseline;
    gap: 3px;
    font-weight: 700;
}

.volume-value {
    font-size: 1rem;
    color: #f97316;
}

.volume-unit {
    font-size: .875rem;
    color: #fb923c;
    font-weight: 500;
}

.volume-primary {
    font-weight: 600;
    color: #FF8C00;
    font-size: .9rem;
}

.equipment-name {
    color: #2196F3;
    font-weight: 500;
}

.metric-value {
    font-weight: 500;
    color: #475569;
}

.personnel-name {
    color: #9C27B0;
    font-weight: 500;
}

.error-text {
    color: #E74C3C;
    font-size: .8rem;
    font-style: italic;
    cursor: help;
}

.count-value {
    font-weight: 700;
    color: #FF8C00;
    font-size: 1rem;
}

.status-pill {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .3px;
}

.status-200 {
    background: rgba(39, 174, 96, .15);
    color: #27AE60;
    border: 1px solid rgba(39, 174, 96, .3);
}

.status-401 {
    background: rgba(231, 76, 60, .15);
    color: #E74C3C;
    border: 1px solid rgba(231, 76, 60, .3);
}

.status-400 {
    background: rgba(255, 152, 0, .15);
    color: #FF9800;
    border: 1px solid rgba(255, 152, 0, .3);
}

.status-422 {
    background: rgba(155, 89, 182, .15);
    color: #9B59B6;
    border: 1px solid rgba(155, 89, 182, .3);
}

.status-default {
    background: rgba(100, 116, 139, .1);
    color: #64748b;
    border: 1px solid rgba(100, 116, 139, .3);
}

.status-cell-new {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.status-dot-new {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.status-dot-new.processed {
    background: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, .2);
    animation: pulse-green 2s infinite;
}

.status-dot-new.failed {
    background: #ef4444;
}

.status-text-new {
    font-size: .8rem;
    color: #10b981;
    font-weight: 600;
}

.text-failed {
    color: #ef4444;
    font-weight: 600;
    font-size: .8rem;
}

@keyframes pulse-green {
    0% {
        box-shadow: 0 0 0 2px rgba(16, 185, 129, .2)
    }

    70% {
        box-shadow: 0 0 0 6px rgba(16, 185, 129, 0)
    }

    100% {
        box-shadow: 0 0 0 2px rgba(16, 185, 129, .2)
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #334155;
    background: #f8fafc;
    border-radius: 8px;
    margin: 20px;
}

.empty-icon {
    font-size: 4rem;
    color: #FFE4B5;
    margin-bottom: 16px;
}

.empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.empty-description {
    color: #6c757d;
    text-align: center;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(241, 245, 249, .95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

.loader {
    width: 48px;
    height: 48px;
    border: 4px solid #FFE4B5;
    border-top: 4px solid #D2691E;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg)
    }

    100% {
        transform: rotate(360deg)
    }
}

.pagination-container {
    padding: 16px 20px;
    border-top: 1px solid #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f1f5f9;
}

.pagination-info {
    color: #6c757d;
    font-size: .875rem;
}

.pagination-controls {
    display: flex;
    gap: 4px;
}

.page-btn {
    padding: 6px 12px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: #495057;
    font-size: .875rem;
    cursor: pointer;
    transition: all .2s;
    font-weight: 500;
    min-width: 40px;
}

.page-btn:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #D2691E;
    color: #D2691E;
}

.page-btn.active {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-color: #D2691E;
}

.page-btn:disabled {
    opacity: .5;
    cursor: not-allowed;
}

.simple-popup {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, .5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.drill-down-overlay {
    z-index: 99999 !important;
    background: rgba(0, 0, 0, .6);
}

.popup-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 700px;
    max-height: 80vh;
    overflow: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, .3);
}

.wide-popup {
    max-width: 900px;
}

.drill-down-popup {
    position: fixed !important;
    max-width: 1200px;
    width: 90%;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0, 0, 0, .4);
    border: 2px solid #FF8C00;
    z-index: 100000;
}

.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-radius: 8px 8px 0 0;
}

.popup-header h3 {
    margin: 0;
    font-size: 1.25rem;
}

.close-btn {
    background: rgba(255, 255, 255, .2);
    border: none;
    color: white;
    font-size: 28px;
    cursor: pointer;
    padding: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all .2s;
    line-height: 1;
}

.close-btn:hover {
    background: rgba(255, 255, 255, .3);
    transform: scale(1.1);
}

.drill-down-header {
    cursor: move;
    user-select: none;
}

.header-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
}

.popup-date-range {
    font-size: .85rem;
    color: rgba(255, 255, 255, .9);
}

.popup-body {
    padding: 20px;
}

.drill-down-popup .popup-body {
    padding: 20px 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.detail-sections {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.detail-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e2e8f0;
}

.detail-section h4 {
    margin: 0 0 12px 0;
    color: #334155;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
}

.source-icon {
    font-size: 1.25rem;
}

.equipment-section {
    border-left: 3px solid #3498DB;
}

.cost-section {
    border-left: 3px solid #FF9800;
}

.personnel-section {
    border-left: 3px solid #9C27B0;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.detail-row {
    font-size: .9rem;
}

.detail-row strong {
    color: #34495E;
    display: inline-block;
    min-width: 100px;
}

.drill-down-summary {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.summary-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.summary-label {
    font-size: .875rem;
    color: #64748b;
    font-weight: 500;
}

.summary-value-large {
    font-size: 1.5rem;
    font-weight: 700;
    color: #FF8C00;
}

.summary-stats {
    display: flex;
    gap: 30px;
    align-items: center;
}

.stat-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.drill-down-actions {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    padding: 0 16px;
}

.action-btn {
    padding: 8px 16px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: white;
    color: #475569;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .2s;
    display: flex;
    align-items: center;
    gap: 6px;
}

.action-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.excel-btn:hover {
    background: #10b981;
    color: white;
    border-color: #10b981;
}

.print-btn:hover {
    background: #8b5cf6;
    color: white;
    border-color: #8b5cf6;
}

.drill-down-grid {
    flex: 1;
    overflow: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin: 0 16px;
}

.drill-down-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
}

.drill-down-table thead {
    background: #f1f5f9;
    position: sticky;
    top: 0;
    z-index: 10;
}

.drill-down-table th {
    padding: 10px;
    text-align: left;
    font-weight: 600;
    font-size: .875rem;
    color: #475569;
    border-bottom: 2px solid #e2e8f0;
}

.drill-down-table tbody tr {
    border-bottom: 1px solid #f0f0f0;
    transition: background .2s;
}

.drill-down-row {
    cursor: pointer;
}

.drill-down-row:hover {
    background: #f8fafc;
}

.drill-down-table td {
    padding: 10px;
    font-size: .875rem;
    color: #334155;
}

.product-badge-small {
    padding: 3px 8px;
    font-size: .75rem;
    border-radius: 12px;
    display: inline-block;
}

.registration-code {
    font-family: monospace;
    font-size: .8rem;
    background: #f0f9ff;
    padding: 2px 6px;
    border-radius: 4px;
    color: #0369a1;
}

.volume-cell-drill {
    font-weight: 600;
    color: #f97316;
    font-size: .9rem;
}

.drill-down-totals {
    padding: 12px 16px;
    background: #f8fafc;
    border-top: 2px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
}

.total-label {
    font-weight: 600;
    color: #475569;
    font-size: .95rem;
}

.total-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #FF8C00;
}

.drill-down-footer {
    padding: 12px 16px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    text-align: right;
    border-radius: 0 0 8px 8px;
}

.record-count {
    font-size: .875rem;
    color: #64748b;
}

.fields-section {
    grid-column: 1 / -1;
}

.fields-table {
    width: 100%;
    border-collapse: collapse;
    font-size: .875rem;
    margin-top: 8px;
}

.fields-table thead {
    background: #f1f5f9;
}

.fields-table th {
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    color: #475569;
    border-bottom: 2px solid #e2e8f0;
}

.fields-table tbody tr {
    border-bottom: 1px solid #f0f4f8;
}

.fields-table tbody tr:hover {
    background: #f8fafc;
}

.field-key {
    padding: 7px 12px;
    font-weight: 600;
    color: #334155;
    width: 40%;
}

.field-value {
    padding: 7px 12px;
    color: #475569;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

@media (max-width: 768px) {
    .main-content {
        padding: 12px
    }

    .stats-container {
        grid-template-columns: 1fr 1fr
    }

    .search-controls {
        grid-template-columns: 1fr
    }

    .data-table {
        font-size: .75rem
    }

    .data-table th,
    .data-table td {
        padding: 8px
    }
}

@media (max-width: 480px) {
    .header-content {
        flex-direction: column;
        gap: 15px
    }

    .stats-container {
        grid-template-columns: 1fr
    }

    .pagination-container {
        flex-direction: column;
        gap: 15px;
        align-items: stretch
    }

    .pagination-controls {
        justify-content: center
    }
}

.manual-process-btn {
    background-color: #28a745;   /* Green */
    color: white;
    border: none;
    padding: 5px 9px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
}

.manual-process-btn:hover {
    background-color: #218838;   /* Darker green on hover */
}

.manual-process-btn:active {
    transform: scale(0.97);
}
</style>